import { useState, useEffect } from "react";
import { KissIcon } from "@/components/BizouLogo";
import { useTranslation } from "@/i18n/LanguageContext";

const COOKIE_CONSENT_KEY = "bizou_cookie_consent";

const getConsent = () => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const saveConsent = (consent) => {
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
    ...consent,
    timestamp: new Date().toISOString(),
  }));
};

const applyConsent = (consent) => {
  if (consent?.analytics) {
    if (window.gtag) window.gtag('consent', 'update', { analytics_storage: 'granted' });
    if (window.clarity) window.clarity('consent', true);
  } else {
    if (window.gtag) window.gtag('consent', 'update', { analytics_storage: 'denied' });
    if (window.clarity) window.clarity('consent', false);
  }
  if (consent?.marketing) {
    if (window.fbq) window.fbq('consent', 'grant');
  } else {
    if (window.fbq) window.fbq('consent', 'revoke');
  }
};

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = getConsent();
    if (consent) {
      applyConsent(consent);
    } else {
      if (window.gtag) window.gtag('consent', 'default', { analytics_storage: 'denied', ad_storage: 'denied' });
      setVisible(true);
    }
  }, []);

  const acceptAll = () => {
    const consent = { analytics: true, marketing: true };
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
  };

  const refuseAll = () => {
    const consent = { analytics: false, marketing: false };
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
  };

  const saveCustom = () => {
    const consent = { analytics, marketing };
    saveConsent(consent);
    applyConsent(consent);
    setVisible(false);
    setShowCustomize(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4" data-testid="cookie-banner">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-[#F0E8EB] p-6">
        {!showCustomize ? (
          <>
            <div className="flex items-start gap-3 mb-4">
              <KissIcon className="w-8 h-8 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-[#1C1917] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("cookie.title")}
                </h3>
                <p className="text-sm text-[#78716C] leading-relaxed">
                  {t("cookie.description")}{" "}
                  <a href="/privacy" className="text-[#E8175D] hover:underline">{t("cookie.learnMore")}</a>
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={refuseAll}
                className="flex-1 h-10 rounded-full border border-[#E7E5E4] text-[#1C1917] font-medium text-sm hover:bg-[#F5F0F2] transition-colors"
                data-testid="cookie-refuse-btn"
              >
                {t("cookie.refuse")}
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="flex-1 h-10 rounded-full border border-[#E7E5E4] text-[#1C1917] font-medium text-sm hover:bg-[#F5F0F2] transition-colors"
                data-testid="cookie-customize-btn"
              >
                {t("cookie.customize")}
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 h-10 rounded-full bg-[#E8175D] text-white font-medium text-sm hover:bg-[#C4134F] transition-colors"
                data-testid="cookie-accept-btn"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="font-semibold text-[#1C1917] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("cookie.customizeTitle")}
            </h3>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-3 bg-[#F5F0F2] rounded-xl">
                <div>
                  <p className="font-medium text-[#1C1917] text-sm">{t("cookie.essential")}</p>
                  <p className="text-xs text-[#78716C]">{t("cookie.essentialDesc")}</p>
                </div>
                <div className="w-10 h-6 bg-[#E8175D] rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F0F2] rounded-xl">
                <div>
                  <p className="font-medium text-[#1C1917] text-sm">{t("cookie.analytics")}</p>
                  <p className="text-xs text-[#78716C]">{t("cookie.analyticsDesc")}</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${analytics ? 'bg-[#E8175D] justify-end' : 'bg-[#E7E5E4] justify-start'}`}
                  data-testid="toggle-analytics"
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
              <div className="flex items-center justify-between p-3 bg-[#F5F0F2] rounded-xl">
                <div>
                  <p className="font-medium text-[#1C1917] text-sm">{t("cookie.marketing")}</p>
                  <p className="text-xs text-[#78716C]">{t("cookie.marketingDesc")}</p>
                </div>
                <button
                  onClick={() => setMarketing(!marketing)}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${marketing ? 'bg-[#E8175D] justify-end' : 'bg-[#E7E5E4] justify-start'}`}
                  data-testid="toggle-marketing"
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCustomize(false)}
                className="flex-1 h-10 rounded-full border border-[#E7E5E4] text-[#1C1917] font-medium text-sm hover:bg-[#F5F0F2]"
              >
                {t("cookie.back")}
              </button>
              <button
                onClick={saveCustom}
                className="flex-1 h-10 rounded-full bg-[#E8175D] text-white font-medium text-sm hover:bg-[#C4134F]"
                data-testid="cookie-save-btn"
              >
                {t("cookie.save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const reopenCookieBanner = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
};
